// The eight bundled courses, two per mode. Hand-authored so the
// experiment layouts are physically meaningful: every reaction the
// narration claims is one the engine actually supports (see
// `EXPERIMENTS.md` for the action contract and the formula list in
// `apps/api/src/prompts.ts` for the canonical reaction set).
//
// Each course is a small choreography of three primitives:
//
//   `experiment` → set up or modify the world
//   `wait`       → let physics + chemistry run for N ticks
//   `narrate`    → pause and let Axie explain what just happened
//
// Coordinate convention follows the engine: 256x256 grid, origin
// top-left, +y down, +x right. Centre is (128, 128). All courses
// run at the standard sandbox dimensions.

import type { Course, CourseStep } from "./types";

const GRID = { width: 256, height: 256 };

type ActionList = Array<{ tick: number; kind: string; params: unknown }>;

// Helper: build a "setup" experiment that wipes the world and lays
// down a starting scene in one tick. Every course's first step uses
// this shape so course state never leaks in from whatever the user
// had on the canvas before.
function setup(name: string, actions: ActionList): CourseStep {
  return {
    kind: "experiment",
    spec: {
      name,
      grid: GRID,
      seed: 0,
      actions: [
        { tick: 0, kind: "clear", params: {} },
        ...actions,
      ],
    },
  };
}

// Helper: build a "modifier" experiment (no clear) — overlays a
// single mid-course action onto the existing world. The engine
// applies the action at tick 0 and stops (no expectations).
function modify(
  name: string,
  action: { kind: string; params: unknown },
): CourseStep {
  return {
    kind: "experiment",
    spec: {
      name,
      grid: GRID,
      seed: 0,
      actions: [{ tick: 0, kind: action.kind, params: action.params }],
    },
  };
}

// ---------------------------------------------------------------
// STARTER
// ---------------------------------------------------------------

const BOIL_WATER: Course = {
  id: "boil_water",
  mode: "starter",
  title: "Boil Water",
  summary: "Heat water until it turns into steam.",
  steps: [
    setup("boil_water_setup", [
      // Stone bowl: floor + two side walls.
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "stone", x1: 96, y1: 200, x2: 160, y2: 204 },
      },
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "stone", x1: 92, y1: 170, x2: 96, y2: 200 },
      },
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "stone", x1: 160, y1: 170, x2: 164, y2: 200 },
      },
      // Cool water inside the bowl.
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "water", x1: 97, y1: 180, x2: 159, y2: 199 },
      },
      // Ground level so things have something to rest on.
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "stone", x1: 0, y1: 250, x2: 255, y2: 255 },
      },
    ]),
    {
      kind: "narrate",
      says:
        "Hi! I've placed cool water in a stone bowl. Water molecules right now are wiggling slowly — that's why it stays liquid. Watch what happens when I add lots of heat!",
      animation: "wave",
    },
    modify("boil_water_heat", {
      kind: "heat",
      // Heat is applied *inside* the water column (y=190 sits in the
      // middle of the y=180..199 water bowl), with a large radius so
      // most of the pot warms in a single tick. At the previous
      // (128, 205) r=10, 1400 K the heat was only touching the stone
      // floor and ~5 rows of water; the upper water cells reached
      // boiling only via slow conduction through the lower layers
      // and the user said it never seemed to get hot enough.
      params: { x: 128, y: 190, r: 25, kelvin: 1800 },
    }),
    { kind: "wait", ticks: 60 },
    {
      kind: "narrate",
      says:
        "The water near the bottom is getting hot — around 373 K (100 °C) it can't stay liquid any more. The molecules vibrate so fast they break free and escape as steam.",
      animation: "happy",
    },
    modify("boil_water_keep_heat", {
      kind: "heat",
      params: { x: 128, y: 190, r: 25, kelvin: 1800 },
    }),
    { kind: "wait", ticks: 60 },
    // A third pulse keeps the column boiling vigorously through the
    // final narration instead of cooling back to plain water as
    // surface cells evaporate to steam and carry their heat with
    // them.
    modify("boil_water_third_pulse", {
      kind: "heat",
      params: { x: 128, y: 190, r: 25, kelvin: 1800 },
    }),
    { kind: "wait", ticks: 120 },
    {
      kind: "narrate",
      says:
        "See those white wisps rising? That's steam — water as a gas. Gases are lighter than liquids, so they float up. This is a phase change: same molecules, very different behaviour!",
    },
  ],
};

const FREEZE_WATER: Course = {
  id: "freeze_water",
  mode: "starter",
  title: "Freeze Water",
  summary: "Cool a pool of water until it turns to ice.",
  steps: [
    setup("freeze_water_setup", [
      // Stone-walled pool, wider and shallower.
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "stone", x1: 64, y1: 190, x2: 192, y2: 196 },
      },
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "stone", x1: 60, y1: 160, x2: 64, y2: 196 },
      },
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "stone", x1: 192, y1: 160, x2: 196, y2: 196 },
      },
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "water", x1: 65, y1: 170, x2: 191, y2: 189 },
      },
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "stone", x1: 0, y1: 250, x2: 255, y2: 255 },
      },
    ]),
    {
      kind: "narrate",
      says:
        "Here's a pool of room-temperature water — about 293 K (20 °C). Liquid water is molecules sliding past each other. Let's slow them down…",
      animation: "wave",
    },
    modify("freeze_water_cool", {
      kind: "set_temperature",
      params: { x: 128, y: 180, r: 80, kelvin: 250 },
    }),
    { kind: "wait", ticks: 80 },
    {
      kind: "narrate",
      says:
        "Below 273 K (0 °C), water can't stay liquid — its molecules lock into a crystal lattice. That's ice forming on the surface and along the walls!",
      animation: "happy",
    },
    { kind: "wait", ticks: 120 },
    {
      kind: "narrate",
      says:
        "Ice is the same H₂O molecules as water, just frozen in place. Heat it back up and they break free again. This is one of the three main states of matter: solid, liquid, gas.",
    },
  ],
};

// ---------------------------------------------------------------
// EXPLORER
// ---------------------------------------------------------------

const BURN_WOOD: Course = {
  id: "burn_wood",
  mode: "explorer",
  title: "Burn Wood",
  summary: "Light a tree on fire and watch combustion.",
  steps: [
    setup("burn_wood_setup", [
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "stone", x1: 0, y1: 250, x2: 255, y2: 255 },
      },
      // Tree in the centre, scaled up a little so the fire has more
      // fuel and the demo lasts a few seconds.
      {
        tick: 0,
        kind: "stamp_shape",
        params: { shape: "tree", x: 128, y: 240, scale: 4 },
      },
    ]),
    {
      kind: "narrate",
      says:
        "This is a wooden tree — basically cellulose, long chains of carbon and hydrogen. Lots of stored chemical energy. Let's release some of it!",
      animation: "wave",
    },
    modify("burn_wood_spark", {
      kind: "paint",
      params: { material: "fire", x: 128, y: 240, r: 3 },
    }),
    { kind: "wait", ticks: 80 },
    {
      kind: "narrate",
      says:
        "Combustion! The reaction is wood + O₂ → CO₂ + H₂O + heat + light. The bright glow is hot carbon — the same physics as an old-fashioned light bulb filament.",
      animation: "happy",
    },
    { kind: "wait", ticks: 200 },
    {
      kind: "narrate",
      says:
        "What's left is ash — minerals the fire couldn't burn. Notice the orange embers? Those are bits of carbon still hot enough to glow. They can relight nearby fuel.",
    },
    { kind: "wait", ticks: 200 },
    {
      kind: "narrate",
      says:
        "Without more fuel, the fire dies out. Combustion always needs three things: fuel, oxygen, and heat. Take any one away and the reaction stops — that's why blankets smother fires!",
    },
  ],
};

const ICE_OVER_LAVA: Course = {
  id: "ice_over_lava",
  mode: "explorer",
  title: "Ice Over Lava",
  summary: "Drop an ice block into a volcano's crater and watch the energy transfer.",
  steps: [
    setup("ice_over_lava_setup", [
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "stone", x1: 0, y1: 250, x2: 255, y2: 255 },
      },
      // Volcano stamp gives us a pyramid with a magma chamber +
      // vertical conduit for free, instead of hand-painting it
      // every time. cy=244 puts the base seal (dy=+1) flush with
      // the painted floor at y=250–255.
      {
        tick: 0,
        kind: "stamp_shape",
        params: { shape: "volcano", x: 128, y: 244, scale: 6 },
      },
    ]),
    {
      kind: "narrate",
      says:
        "Here's a volcano — a stepped stone pyramid pre-filled with a column of molten lava from the magma chamber at the base up through the summit (around 1500 K). Now watch what happens when I drop a block of ice on top of it…",
      animation: "wave",
    },
    // The volcano's conduit is pre-filled with lava on tick 0, so
    // no need to wait for the eruption to climb; we just let the
    // cloner produce a bit of overflow first for visual context.
    { kind: "wait", ticks: 60 },
    // ice_block has behavior:solid, so it falls straight down under
    // gravity (unlike plain `ice` which is static and would hover).
    // Small column (4 cells wide × 8 tall) centred on the lava cap
    // — small enough that even at scale=6 the cap's thermal mass
    // overwhelms it; every ice cell flash-vaporises on contact via
    // `ice_block_lava_melt` without dragging the cap's temperature
    // anywhere near the 973 K freezing point.
    modify("ice_over_lava_drop", {
      kind: "paint_rect",
      params: { material: "ice_block", x1: 126, y1: 60, x2: 129, y2: 67 },
    }),
    { kind: "wait", ticks: 90 },
    {
      kind: "narrate",
      says:
        "The ice block is falling toward the crater under gravity. Once it touches the lava it'll flash through all three phases — solid ice → liquid water → gaseous steam — in seconds.",
    },
    { kind: "wait", ticks: 180 },
    {
      kind: "narrate",
      says:
        "Look at all that steam! Heat is energy moving from hot to cold; the lava had energy to spare. Real volcanoes can flash-vaporise lakes the same way.",
      animation: "happy",
    },
  ],
};

// ---------------------------------------------------------------
// INVENTOR
// ---------------------------------------------------------------

const VOLCANO: Course = {
  id: "volcano",
  mode: "inventor",
  title: "Volcano Eruption",
  summary: "Stamp a volcano shape and watch magma rise through the conduit from the chamber and overflow the rim.",
  steps: [
    setup("volcano_setup", [
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "stone", x1: 0, y1: 250, x2: 255, y2: 255 },
      },
      {
        tick: 0,
        kind: "stamp_shape",
        params: { shape: "volcano", x: 128, y: 244, scale: 6 },
      },
    ]),
    {
      kind: "narrate",
      says:
        "I stamped the Volcano shape — a stepped stone pyramid pre-filled with a continuous column of molten lava from the wide magma chamber at the base up to the summit, and one cloner sitting at the centre of the summit with open sky above it. Lava is the cloner's only non-empty non-cloner neighbour, so every trigger pumps a fresh lava cell straight out of the crater.",
      animation: "wave",
    },
    { kind: "wait", ticks: 120 },
    {
      kind: "narrate",
      says:
        "Eruption! Each new lava cell the cloner emits lands on the summit, spreads sideways under gravity, and tips off the staircase rim — running down the outer slopes step by step, left and right, exactly like a real lava flow.",
      animation: "happy",
    },
    { kind: "wait", ticks: 240 },
    // Bonus: pump some extra heat into the upper conduit so the
    // lava stays runnier and flows further down the slopes
    // before freezing back to basalt.
    modify("volcano_heat", {
      kind: "heat",
      params: { x: 128, y: 220, r: 14, kelvin: 1800 },
    }),
    { kind: "wait", ticks: 150 },
    {
      kind: "narrate",
      says:
        "Adding heat lowers the lava's viscosity, so it flows further before solidifying. As it reaches cool stone it freezes back to rock — exactly how real volcanoes build new land over millennia.",
    },
  ],
};

const TORCH_LINE: Course = {
  id: "torch_line",
  mode: "inventor",
  title: "Powder Fuse",
  summary: "Light one end of a gunpowder trail and watch the chain reaction.",
  steps: [
    setup("torch_line_setup", [
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "stone", x1: 0, y1: 245, x2: 255, y2: 255 },
      },
      {
        tick: 0,
        kind: "paint_line",
        params: {
          material: "gunpowder",
          x1: 30,
          y1: 244,
          x2: 226,
          y2: 244,
          thickness: 2,
        },
      },
    ]),
    {
      kind: "narrate",
      says:
        "I've drawn a horizontal line of gunpowder. Gunpowder is a fast fuel — saltpeter + charcoal + sulfur. One spark on one end should propagate the whole length.",
      animation: "wave",
    },
    modify("torch_line_spark", {
      kind: "paint",
      params: { material: "fire", x: 30, y: 244, r: 2 },
    }),
    { kind: "wait", ticks: 60 },
    {
      kind: "narrate",
      says:
        "The flame is racing along the trail. Each burning grain heats the next one above its ignition point, so the reaction sustains itself — a chain reaction!",
      animation: "happy",
    },
    { kind: "wait", ticks: 200 },
    {
      kind: "narrate",
      says:
        "All that's left is a thin streak of ash and smoke. This is the same principle behind every fuse, every line of dominos, every wildfire — local energy unlocking the next bit of fuel.",
    },
  ],
};

// ---------------------------------------------------------------
// GENIUS
// ---------------------------------------------------------------

const HYDROGEN_OXYGEN: Course = {
  id: "hydrogen_oxygen",
  mode: "genius",
  title: "Hydrogen Meets Oxygen",
  summary: "The rocket-fuel reaction: 2 H₂ + O₂ → 2 H₂O.",
  steps: [
    setup("h2_o2_setup", [
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "stone", x1: 0, y1: 250, x2: 255, y2: 255 },
      },
      // Stone chamber with two gas pockets.
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "stone", x1: 80, y1: 220, x2: 176, y2: 224 },
      },
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "stone", x1: 76, y1: 150, x2: 80, y2: 224 },
      },
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "stone", x1: 176, y1: 150, x2: 180, y2: 224 },
      },
      // Hydrogen on the left half.
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "hydrogen", x1: 82, y1: 160, x2: 126, y2: 219 },
      },
      // Oxygen on the right half.
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "oxygen", x1: 130, y1: 160, x2: 174, y2: 219 },
      },
    ]),
    {
      kind: "narrate",
      says:
        "Two gases in one chamber: hydrogen (H₂) on the left, oxygen (O₂) on the right. By themselves they're stable. But give them a spark and they want to combine — *fast*.",
      animation: "wave",
    },
    modify("h2_o2_ignite", {
      kind: "heat",
      params: { x: 128, y: 200, r: 8, kelvin: 1500 },
    }),
    { kind: "wait", ticks: 60 },
    {
      kind: "narrate",
      says:
        "Boom! The reaction is 2 H₂ + O₂ → 2 H₂O. Two hydrogen molecules join one oxygen to make two water molecules — and release a *lot* of heat. This is what powers Space-X rocket engines.",
      animation: "happy",
    },
    { kind: "wait", ticks: 150 },
    {
      kind: "narrate",
      says:
        "The hot water vapour rises as steam. As it cools, it would condense back into liquid water. Every shuttle launch you've seen produced clouds of water — this exact reaction.",
    },
  ],
};

const ACID_BASE: Course = {
  id: "acid_base",
  mode: "genius",
  title: "Acid Meets Base",
  summary: "Neutralisation: HCl + NaOH → H₂O + NaCl.",
  steps: [
    setup("acid_base_setup", [
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "stone", x1: 0, y1: 250, x2: 255, y2: 255 },
      },
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "stone", x1: 60, y1: 210, x2: 196, y2: 214 },
      },
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "stone", x1: 56, y1: 160, x2: 60, y2: 214 },
      },
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "stone", x1: 196, y1: 160, x2: 200, y2: 214 },
      },
      // Centre wall separating the two halves.
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "stone", x1: 126, y1: 180, x2: 130, y2: 210 },
      },
      {
        tick: 0,
        kind: "paint_rect",
        params: {
          material: "hydrochloric_acid",
          x1: 61,
          y1: 180,
          x2: 125,
          y2: 209,
        },
      },
      {
        tick: 0,
        kind: "paint_rect",
        params: {
          material: "sodium_hydroxide",
          x1: 131,
          y1: 180,
          x2: 195,
          y2: 209,
        },
      },
    ]),
    {
      kind: "narrate",
      says:
        "On the left: hydrochloric acid (HCl) — the same acid your stomach uses to digest food. On the right: sodium hydroxide (NaOH) — a strong base used to make soap. Wall in the middle keeps them apart.",
      animation: "wave",
    },
    modify("acid_base_open", {
      kind: "clear",
      params: { x: 128, y: 195, r: 8 },
    }),
    { kind: "wait", ticks: 60 },
    {
      kind: "narrate",
      says:
        "I removed the wall. The acid and base are mixing — and reacting: HCl + NaOH → H₂O + NaCl. They neutralise each other, producing pure water and ordinary table salt.",
      animation: "happy",
    },
    { kind: "wait", ticks: 150 },
    {
      kind: "narrate",
      says:
        "Two corrosive chemicals cancelled each other out. This is exactly how antacid tablets work — a mild base neutralising stomach acid. Chemistry is everywhere, often beautifully balanced.",
    },
  ],
};

// ===============================================================
// SECOND BATCH — 2 more per mode. Same primitives, broader range
// of materials and reactions. Pulls from the granular-physics,
// dissolution, oxidation, and alkali-metal sides of the engine.
// ===============================================================

// ---------------------------------------------------------------
// STARTER (extra)
// ---------------------------------------------------------------

const FALLING_SAND: Course = {
  id: "falling_sand",
  mode: "starter",
  title: "Falling Sand",
  summary: "Stack sand on a stone shelf with a gap and watch it pour.",
  steps: [
    setup("falling_sand_setup", [
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "stone", x1: 0, y1: 250, x2: 255, y2: 255 },
      },
      // Stone shelf with a gap in the middle so the sand has
      // somewhere to fall into.
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "stone", x1: 40, y1: 170, x2: 116, y2: 174 },
      },
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "stone", x1: 140, y1: 170, x2: 216, y2: 174 },
      },
      // Pile of sand on top of the shelf.
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "sand", x1: 50, y1: 130, x2: 206, y2: 169 },
      },
    ]),
    {
      kind: "narrate",
      says:
        "Here's a pile of sand on a stone shelf with a gap in the middle. Sand is a granular powder — each grain falls under gravity but piles up against its neighbours.",
      animation: "wave",
    },
    { kind: "wait", ticks: 60 },
    {
      kind: "narrate",
      says:
        "Watch the grains tumble through the gap! Notice how they form a neat conical pile at the bottom — that's the angle of repose, the steepest slope a powder can hold without sliding.",
      animation: "happy",
    },
    { kind: "wait", ticks: 200 },
    {
      kind: "narrate",
      says:
        "Eventually everything settles. This is the same physics that runs hourglasses, sand dunes, and avalanches — granular materials behave like liquids when flowing and like solids at rest.",
    },
  ],
};

const LIGHT_CANDLE: Course = {
  id: "light_candle",
  mode: "starter",
  title: "Light a Candle",
  summary: "Stamp a candle and watch its wax + wick + flame burn together.",
  steps: [
    setup("light_candle_setup", [
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "stone", x1: 0, y1: 250, x2: 255, y2: 255 },
      },
      // Stamp candle gives us wax pillar + rope wick + a fire on
      // top, all at once.
      {
        tick: 0,
        kind: "stamp_shape",
        params: { shape: "candle", x: 128, y: 245, scale: 6 },
      },
    ]),
    {
      kind: "narrate",
      says:
        "A candle: a tower of wax, a rope wick, and a flame already lit on top. Three different materials all working together!",
      animation: "wave",
    },
    { kind: "wait", ticks: 120 },
    {
      kind: "narrate",
      says:
        "Watch how the heat from the flame slowly melts the wax beneath it. The wax is the candle's fuel — the wick acts like a straw, drawing melted wax up to where it can burn.",
      animation: "happy",
    },
    { kind: "wait", ticks: 200 },
    {
      kind: "narrate",
      says:
        "A real candle burns for hours because the wax is consumed slowly. Same chemistry as a campfire (combustion of carbon-rich fuel + oxygen) — just way more controlled.",
    },
  ],
};

// ---------------------------------------------------------------
// EXPLORER (extra)
// ---------------------------------------------------------------

const SALT_DISSOLVES: Course = {
  id: "salt_dissolves",
  mode: "explorer",
  title: "Salt Dissolves",
  summary: "Drop salt into water and watch it disappear into solution.",
  steps: [
    setup("salt_dissolves_setup", [
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "stone", x1: 0, y1: 250, x2: 255, y2: 255 },
      },
      // Wider, shallower pool so the salt has lots of water to
      // disperse into.
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "stone", x1: 60, y1: 200, x2: 196, y2: 204 },
      },
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "stone", x1: 56, y1: 170, x2: 60, y2: 204 },
      },
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "stone", x1: 196, y1: 170, x2: 200, y2: 204 },
      },
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "water", x1: 61, y1: 180, x2: 195, y2: 199 },
      },
    ]),
    {
      kind: "narrate",
      says:
        "A pool of plain water. Now I'll sprinkle some table salt on top — let's see what happens when an ionic solid meets a polar solvent.",
      animation: "wave",
    },
    modify("salt_dissolves_drop", {
      kind: "paint_rect",
      params: { material: "salt", x1: 90, y1: 150, x2: 166, y2: 158 },
    }),
    { kind: "wait", ticks: 80 },
    {
      kind: "narrate",
      says:
        "The salt is sinking and dissolving! Water molecules pull the sodium and chloride ions apart and surround them — that's solvation. The salt doesn't disappear, it spreads out.",
      animation: "happy",
    },
    { kind: "wait", ticks: 200 },
    {
      kind: "narrate",
      says:
        "Stir long enough and you can't see the salt at all — but taste a drop and it's clearly there. Same trick the ocean uses to hold ~35 grams of dissolved salt per litre.",
    },
  ],
};

const IRON_RUSTS: Course = {
  id: "iron_rusts",
  mode: "explorer",
  title: "Iron Rusts",
  summary: "Surround an iron block with oxygen and watch oxidation eat it.",
  steps: [
    setup("iron_rusts_setup", [
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "stone", x1: 0, y1: 250, x2: 255, y2: 255 },
      },
      // Iron slab in the middle.
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "iron", x1: 96, y1: 180, x2: 160, y2: 220 },
      },
      // Oxygen atmosphere all around it.
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "oxygen", x1: 40, y1: 100, x2: 95, y2: 240 },
      },
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "oxygen", x1: 161, y1: 100, x2: 216, y2: 240 },
      },
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "oxygen", x1: 40, y1: 100, x2: 216, y2: 175 },
      },
    ]),
    {
      kind: "narrate",
      says:
        "An iron block surrounded by pure oxygen. Iron + oxygen is one of the slowest-burning combustions there is — it takes years in the real world. We'll cheat with concentrated O₂.",
      animation: "wave",
    },
    { kind: "wait", ticks: 200 },
    {
      kind: "narrate",
      says:
        "Look closely at the iron's surface — those reddish-brown patches are rust forming! Reaction: 4Fe + 3O₂ → 2Fe₂O₃. The metal literally combines with the air.",
      animation: "happy",
    },
    { kind: "wait", ticks: 300 },
    {
      kind: "narrate",
      says:
        "This is why bridges, cars, and ships need paint — paint blocks oxygen from touching the iron. Same chemistry, just denied a reactant. No oxygen, no rust!",
    },
  ],
};

// ---------------------------------------------------------------
// INVENTOR (extra)
// ---------------------------------------------------------------

const MOUNTAIN_WINTER: Course = {
  id: "mountain_winter",
  mode: "inventor",
  title: "Mountain in Winter",
  summary: "Stamp a snow-capped mountain and explore why the snow stays frozen at altitude.",
  steps: [
    setup("mountain_winter_setup", [
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "stone", x1: 0, y1: 250, x2: 255, y2: 255 },
      },
      {
        tick: 0,
        kind: "stamp_shape",
        params: { shape: "mountain", x: 128, y: 235, scale: 6 },
      },
      // The world's ambient temperature is 293 K (20 °C), well
      // above the 273.15 K (0 °C) at which snow melts to water.
      // Without this cooling step the snow on the peak crossed
      // its melting point on the very first tick and ran to the
      // valley as water. Real peak snow stays frozen because the
      // *air* at altitude is below freezing — the lapse rate is
      // about 6.5 °C per kilometre, so a peak ~4 km above the
      // valley sits around –10 °C (263 K). Pin the whole
      // mountain to that temperature in the setup so the
      // simulation models the altitude effect.
      {
        tick: 0,
        kind: "set_temperature",
        params: { x: 128, y: 225, r: 22, kelvin: 263 },
      },
    ]),
    {
      kind: "narrate",
      says:
        "This is a mountain — stone slopes capped with snow. Snow is just H₂O (water) frozen into crystals. We're high up here, about 4 km above the valley floor; the air gets ~6.5 °C colder for every kilometre you climb (that's called the lapse rate), so the peak sits around −10 °C (263 K) even when the valley is a comfortable 20 °C (293 K).",
      animation: "wave",
    },
    { kind: "wait", ticks: 120 },
    {
      kind: "narrate",
      says:
        "Now I'll heat the base of the mountain to about 47 °C (320 K), like a hot summer afternoon. H₂O melts at 0 °C (273 K), so if the warmth reaches the peak the snow will run as water. Does it?",
    },
    modify("mountain_winter_warm", {
      kind: "heat",
      params: { x: 128, y: 240, r: 14, kelvin: 320 },
    }),
    { kind: "wait", ticks: 200 },
    {
      kind: "narrate",
      says:
        "The cap is still there! Stone is a poor heat conductor and has a high heat capacity, so it soaks up warmth before passing it upward. By the time any of that heat reaches the peak, it's been spent warming the lower slopes — and the snow stays below 0 °C (273 K), never crossing H₂O's melting point. That's why glaciers persist on tall peaks even when the valleys below are warm.",
      animation: "happy",
    },
  ],
};

const POOL_BY_BONFIRE: Course = {
  id: "pool_by_bonfire",
  mode: "inventor",
  title: "Pool Beside a Bonfire",
  summary: "Place a pool next to a roaring bonfire and watch heat radiate.",
  steps: [
    setup("pool_by_bonfire_setup", [
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "stone", x1: 0, y1: 250, x2: 255, y2: 255 },
      },
      // Bonfire on the left side.
      {
        tick: 0,
        kind: "stamp_shape",
        params: { shape: "bonfire", x: 80, y: 245, scale: 5 },
      },
      // Pool on the right side.
      {
        tick: 0,
        kind: "stamp_shape",
        params: { shape: "pool", x: 180, y: 240, scale: 5 },
      },
    ]),
    {
      kind: "narrate",
      says:
        "A roaring bonfire on the left, a stone-walled pool of water on the right. Two complete scenes built from a single shape stamp each.",
      animation: "wave",
    },
    { kind: "wait", ticks: 120 },
    {
      kind: "narrate",
      says:
        "The bonfire's flame is hot enough to glow — that orange light is incandescence. Heat radiates outward in all directions, warming nearby air and surfaces.",
    },
    { kind: "wait", ticks: 200 },
    {
      kind: "narrate",
      says:
        "Build something! The stamp_shape system in the engine has torch, candle, bonfire, tree, volcano, mountain, and pool — combine them to tell whatever story you like.",
      animation: "happy",
    },
  ],
};

// ---------------------------------------------------------------
// GENIUS (extra)
// ---------------------------------------------------------------

const METHANE_COMBUSTION: Course = {
  id: "methane_combustion",
  mode: "genius",
  title: "Methane Combustion",
  summary: "The kitchen-stove reaction: CH₄ + 2 O₂ → CO₂ + 2 H₂O.",
  steps: [
    setup("methane_combustion_setup", [
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "stone", x1: 0, y1: 250, x2: 255, y2: 255 },
      },
      // Stone chamber to hold the gas mixture.
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "stone", x1: 80, y1: 220, x2: 176, y2: 224 },
      },
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "stone", x1: 76, y1: 150, x2: 80, y2: 224 },
      },
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "stone", x1: 176, y1: 150, x2: 180, y2: 224 },
      },
      // Methane on the bottom (denser-than-air for layout).
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "methane", x1: 82, y1: 195, x2: 174, y2: 219 },
      },
      // Oxygen layer above so they can mix when ignited.
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "oxygen", x1: 82, y1: 160, x2: 174, y2: 194 },
      },
    ]),
    {
      kind: "narrate",
      says:
        "Methane (CH₄) below, oxygen above. This is the same fuel that runs gas stoves and natural-gas power plants — completely safe until you add a spark.",
      animation: "wave",
    },
    modify("methane_combustion_spark", {
      kind: "heat",
      params: { x: 128, y: 190, r: 6, kelvin: 1200 },
    }),
    { kind: "wait", ticks: 80 },
    {
      kind: "narrate",
      says:
        "Boom! CH₄ + 2 O₂ → CO₂ + 2 H₂O + heat. One methane molecule plus two oxygens makes carbon dioxide and water vapour, releasing energy that drove the explosion.",
      animation: "happy",
    },
    { kind: "wait", ticks: 150 },
    {
      kind: "narrate",
      says:
        "The water vapour you see is real — every kg of methane burned makes 2.25 kg of water. That's why your gas hob's flame leaves a wet ring on a cold lid.",
    },
  ],
};

const SODIUM_IN_WATER: Course = {
  id: "sodium_in_water",
  mode: "genius",
  title: "Sodium Meets Water",
  summary: "Drop alkali metal into water for a violent hydrogen-releasing reaction.",
  steps: [
    setup("sodium_in_water_setup", [
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "stone", x1: 0, y1: 250, x2: 255, y2: 255 },
      },
      // Wide stone-walled pool of water.
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "stone", x1: 50, y1: 210, x2: 206, y2: 214 },
      },
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "stone", x1: 46, y1: 170, x2: 50, y2: 214 },
      },
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "stone", x1: 206, y1: 170, x2: 210, y2: 214 },
      },
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "water", x1: 51, y1: 180, x2: 205, y2: 209 },
      },
    ]),
    {
      kind: "narrate",
      says:
        "A pool of water. Now I'll drop in a chunk of sodium — an alkali metal so reactive it has to be stored under oil to keep it away from air and moisture.",
      animation: "wave",
    },
    modify("sodium_in_water_drop", {
      kind: "paint_rect",
      // Drop the chunk right above the water surface (water spans
      // y=180..209) and make it a smaller 10×12 block. The old
      // 20×20 block at y=130..150 had to fall ~30 cells before
      // anything happened, then the chunk was so thick that only
      // the outer perimeter was reacting at any given tick — the
      // user perceived the whole sequence as taking forever.
      // With the chunk starting at y=168 the bottom row hits
      // water in ~12 ticks and the `sodium_ignites_near_fire`
      // decay added in reactions.yaml lights up the interior
      // almost immediately after.
      params: { material: "sodium", x1: 123, y1: 168, x2: 132, y2: 179 },
    }),
    { kind: "wait", ticks: 60 },
    {
      kind: "narrate",
      says:
        "The reaction! 2 Na + 2 H₂O → 2 NaOH + H₂↑ + heat. Sodium hands an electron to water, releasing hydrogen gas — and the heat is often enough to ignite that hydrogen.",
      animation: "happy",
    },
    { kind: "wait", ticks: 200 },
    {
      kind: "narrate",
      says:
        "What's left in the pool is sodium hydroxide (lye) dissolved in water — a strong base. Same chemistry pattern as lithium and potassium, just less spectacular than caesium (which boils on contact!).",
    },
  ],
};

// ---------------------------------------------------------------
// New courses exercising the recent density-aware physics upgrades:
//   * `MERCURY_SINKS` and `OIL_FLOATS` exercise denser-sinks
//     liquid stratification (PR #37, `try_fall_down` density swap).
//   * `IRON_CRUSHES_WOOD` exercises density-gated crushing in
//     `apply_rigid_body_fall`.
//   * `CAESIUM_EXPLODES` exercises the caesium + water reaction
//     suite added in PR #34 (fire + cloud + chain-ignite + blast).
// All four are short and self-contained so they're useful as
// smoke-tests after future engine changes.

const MERCURY_SINKS: Course = {
  id: "mercury_sinks",
  mode: "genius",
  title: "Mercury Sinks in Water",
  summary: "Pour mercury onto water and watch the heavier liquid fall through.",
  steps: [
    setup("mercury_sinks_setup", [
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "stone", x1: 0, y1: 250, x2: 255, y2: 255 },
      },
      // Tall narrow stone-walled tank so the liquids can't drain
      // sideways. y=120..249 (130 rows tall) gives plenty of room
      // to see mercury work its way down through the water column.
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "stone", x1: 90, y1: 120, x2: 96, y2: 250 },
      },
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "stone", x1: 160, y1: 120, x2: 166, y2: 250 },
      },
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "water", x1: 97, y1: 130, x2: 159, y2: 249 },
      },
    ]),
    {
      kind: "narrate",
      says:
        "A tank of water. In a moment I'll pour mercury on top — mercury is one of the few metals that's liquid at room temperature, and it's incredibly dense (~13× denser than water).",
      animation: "wave",
    },
    modify("mercury_sinks_drop", {
      kind: "paint_rect",
      params: { material: "mercury", x1: 122, y1: 130, x2: 135, y2: 135 },
    }),
    { kind: "wait", ticks: 80 },
    {
      kind: "narrate",
      says:
        "Look! The mercury isn't sitting on the surface like a cork — it's punching down through the water. Gravity pulls everything down, but the *denser* fluid wins the race: mercury falls, water rises.",
      animation: "happy",
    },
    { kind: "wait", ticks: 200 },
    {
      kind: "narrate",
      says:
        "Mercury (~13546 kg/m³) ends up pooled at the bottom; water (1000) floats on top of it. Same gravity acting on both, but density is what decides which one ends up on the floor.",
    },
  ],
};

const OIL_FLOATS: Course = {
  id: "oil_floats",
  mode: "inventor",
  title: "Oil Floats on Water",
  summary:
    "Inject oil at the bottom of a pool and watch the lighter liquid rise to the surface.",
  steps: [
    setup("oil_floats_setup", [
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "stone", x1: 0, y1: 250, x2: 255, y2: 255 },
      },
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "stone", x1: 60, y1: 150, x2: 64, y2: 250 },
      },
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "stone", x1: 192, y1: 150, x2: 196, y2: 250 },
      },
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "stone", x1: 60, y1: 246, x2: 196, y2: 250 },
      },
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "water", x1: 65, y1: 160, x2: 191, y2: 245 },
      },
    ]),
    {
      kind: "narrate",
      says:
        "A pool of water. Now I'll inject a blob of oil right at the *bottom* of the pool. Oil is less dense than water (~870 vs 1000 kg/m³).",
      animation: "wave",
    },
    modify("oil_floats_inject", {
      kind: "paint_rect",
      params: { material: "oil", x1: 110, y1: 235, x2: 145, y2: 244 },
    }),
    { kind: "wait", ticks: 250 },
    {
      kind: "narrate",
      says:
        "See the oil climbing? Same rule as the mercury course, just the other direction — water below the oil is denser, so water sinks past the oil and the oil ends up at the surface. This is exactly why oil spills float on the ocean.",
      animation: "happy",
    },
  ],
};

const IRON_CRUSHES_WOOD: Course = {
  id: "iron_crushes_wood",
  mode: "inventor",
  title: "Iron Crushes Wood",
  summary:
    "Drop a heavy iron block onto a wooden plank and watch the wood splinter under the impact.",
  steps: [
    setup("iron_crushes_wood_setup", [
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "stone", x1: 0, y1: 250, x2: 255, y2: 255 },
      },
      // Two stone pillars holding the plank up. The pillars are
      // wider than the iron block so the iron lands on plank, not
      // on stone directly.
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "stone", x1: 80, y1: 200, x2: 90, y2: 250 },
      },
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "stone", x1: 166, y1: 200, x2: 176, y2: 250 },
      },
      // Wooden plank spanning between the pillars.
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "wood", x1: 90, y1: 198, x2: 166, y2: 200 },
      },
    ]),
    {
      kind: "narrate",
      says:
        "A wooden plank resting on two stone pillars. Wood is solid but light — about 600 kg/m³, less than water. Now I'll drop a chunk of iron on it.",
      animation: "wave",
    },
    modify("iron_crushes_wood_drop", {
      kind: "paint_rect",
      params: { material: "iron", x1: 118, y1: 80, x2: 138, y2: 100 },
    }),
    { kind: "wait", ticks: 80 },
    {
      kind: "narrate",
      says:
        "Iron is about 13× denser than wood (7874 vs 600 kg/m³). The engine treats anything that light as crushable when something this heavy hits it — the wood splinters where the block lands and the iron keeps falling.",
      animation: "happy",
    },
    { kind: "wait", ticks: 200 },
    {
      kind: "narrate",
      says:
        "The iron came to rest on the stone floor; the wooden plank survives only where the iron didn't pass through. Stone itself is never crushed — it's the fixed scenery the physics layer treats as bedrock.",
    },
  ],
};

const CAESIUM_EXPLODES: Course = {
  id: "caesium_explodes",
  mode: "genius",
  title: "Caesium Meets Water",
  summary:
    "Toss the most reactive alkali metal into water for an explosive flash and a white aerosol plume.",
  steps: [
    setup("caesium_explodes_setup", [
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "stone", x1: 0, y1: 250, x2: 255, y2: 255 },
      },
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "stone", x1: 40, y1: 220, x2: 216, y2: 224 },
      },
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "stone", x1: 36, y1: 160, x2: 40, y2: 224 },
      },
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "stone", x1: 216, y1: 160, x2: 220, y2: 224 },
      },
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "water", x1: 41, y1: 170, x2: 215, y2: 219 },
      },
    ]),
    {
      kind: "narrate",
      says:
        "A pool of water. Caesium (Cs) sits at the bottom of group 1 on the periodic table — the most reactive alkali metal you'd ever realistically hold. It even melts at body temperature.",
      animation: "wave",
    },
    modify("caesium_explodes_drop", {
      kind: "paint_rect",
      params: { material: "caesium", x1: 124, y1: 156, x2: 131, y2: 168 },
    }),
    { kind: "wait", ticks: 40 },
    {
      kind: "narrate",
      says:
        "Boom! 2 Cs + 2 H₂O → 2 CsOH + H₂↑, and the released hydrogen detonates immediately on the heat of the reaction itself. That bright flash is the fireball; the white plume is caesium hydroxide aerosol.",
      animation: "happy",
    },
    { kind: "wait", ticks: 250 },
    {
      kind: "narrate",
      says:
        "Compare this with the Sodium course: same chemistry pattern, but caesium reacts so fast the explosion happens almost the instant it touches the surface. Sodium is dangerous; caesium is on a different level.",
    },
  ],
};

// Heating snow walks the full phase ladder: solid snow → liquid water
// (above 0 °C / 273 K) → gaseous steam (above 100 °C / 373 K). Two
// distinct phase changes in one pile, which is what makes it a better
// teaching scene than boiling plain water.
const HEAT_THE_SNOW: Course = {
  id: "heat_the_snow",
  mode: "explorer",
  title: "Heat the Snow",
  summary: "Melt a snow pile to water, then boil it to steam.",
  steps: [
    setup("heat_the_snow_setup", [
      // Stone bowl: floor + two side walls to hold the melt.
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "stone", x1: 96, y1: 200, x2: 160, y2: 204 },
      },
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "stone", x1: 92, y1: 168, x2: 96, y2: 200 },
      },
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "stone", x1: 160, y1: 168, x2: 164, y2: 200 },
      },
      // A heaped mound of snow filling the bowl.
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "snow", x1: 97, y1: 176, x2: 159, y2: 199 },
      },
      // Ground level.
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "stone", x1: 0, y1: 250, x2: 255, y2: 255 },
      },
    ]),
    {
      kind: "narrate",
      says:
        "Here's a bowl of snow. Snow is just frozen water — the molecules are locked into a loose crystal, barely moving. It holds its shape because it's below freezing. Let's warm it up gently and see.",
      animation: "wave",
    },
    // First a gentle warmth: enough to cross 0 °C (273 K) so the snow
    // melts to water, but nowhere near boiling.
    modify("heat_the_snow_melt", {
      kind: "heat",
      params: { x: 128, y: 188, r: 26, kelvin: 330 },
    }),
    { kind: "wait", ticks: 90 },
    {
      kind: "narrate",
      says:
        "Above 0 °C (273 K) the crystal can't hold together — the snow melts into liquid water and pools in the bottom of the bowl. That's the first phase change: solid → liquid.",
      animation: "happy",
    },
    // Now crank the heat to push the melt past boiling.
    modify("heat_the_snow_boil", {
      kind: "heat",
      params: { x: 128, y: 192, r: 26, kelvin: 1800 },
    }),
    { kind: "wait", ticks: 80 },
    modify("heat_the_snow_keep_heat", {
      kind: "heat",
      params: { x: 128, y: 192, r: 26, kelvin: 1800 },
    }),
    { kind: "wait", ticks: 120 },
    {
      kind: "narrate",
      says:
        "And past 100 °C (373 K) the water boils away as steam. One little pile of snow just showed you all three states of matter — solid, liquid, gas — the full phase ladder, driven purely by temperature.",
    },
  ],
};

// The kitchen-classic eruption: baking soda at the bottom of a bottle,
// vinegar poured in. NaHCO3 + CH3COOH -> CH3COONa + H2O + CO2↑. The CO2
// is released the instant they touch and its strong upward wind throws
// the foam vertically out of the narrow neck — a sudden bottle volcano.
const BOTTLE_VOLCANO: Course = {
  id: "bottle_volcano",
  mode: "inventor",
  title: "Bottle Volcano",
  summary: "Pour vinegar on baking soda and watch it erupt out of the bottle.",
  steps: [
    setup("bottle_volcano_setup", [
      // Bottle base: floor + two tall side walls.
      { tick: 0, kind: "paint_rect", params: { material: "stone", x1: 110, y1: 224, x2: 146, y2: 228 } },
      { tick: 0, kind: "paint_rect", params: { material: "stone", x1: 110, y1: 170, x2: 114, y2: 224 } },
      { tick: 0, kind: "paint_rect", params: { material: "stone", x1: 142, y1: 170, x2: 146, y2: 224 } },
      // Shoulders that taper the base into a narrow neck.
      { tick: 0, kind: "paint_rect", params: { material: "stone", x1: 114, y1: 170, x2: 122, y2: 174 } },
      { tick: 0, kind: "paint_rect", params: { material: "stone", x1: 134, y1: 170, x2: 142, y2: 174 } },
      // Neck walls (opening is x≈123..133, y150..170).
      { tick: 0, kind: "paint_rect", params: { material: "stone", x1: 120, y1: 150, x2: 122, y2: 170 } },
      { tick: 0, kind: "paint_rect", params: { material: "stone", x1: 134, y1: 150, x2: 136, y2: 170 } },
      // Baking soda heaped in the bottom of the bottle.
      { tick: 0, kind: "paint_rect", params: { material: "baking_soda", x1: 115, y1: 210, x2: 141, y2: 223 } },
      // Ground.
      { tick: 0, kind: "paint_rect", params: { material: "stone", x1: 0, y1: 250, x2: 255, y2: 255 } },
    ]),
    {
      kind: "narrate",
      says:
        "Here's a bottle with baking soda — sodium bicarbonate, NaHCO₃ — packed in the bottom. On its own it just sits there. Now watch what happens the moment I pour vinegar in...",
      animation: "wave",
    },
    // Pour vinegar down the neck; it falls onto the soda and reacts.
    modify("bottle_volcano_pour", {
      kind: "paint_rect",
      params: { material: "vinegar", x1: 123, y1: 152, x2: 133, y2: 178 },
    }),
    { kind: "wait", ticks: 50 },
    {
      kind: "narrate",
      says:
        "Vinegar is dilute acetic acid (CH₃COOH). Acid + bicarbonate react instantly: CH₃COOH + NaHCO₃ → sodium acetate + water + carbon dioxide gas. All that CO₂ is released at once and blasts straight up the neck.",
      animation: "happy",
    },
    { kind: "wait", ticks: 90 },
    {
      kind: "narrate",
      says:
        "That sudden vertical gush is the CO₂ foam erupting out of the bottle — the same reaction behind a classroom volcano. No heat needed: it's pure acid-base chemistry making gas faster than the bottle can hold it.",
    },
  ],
};

// Kerosene is a flammable liquid fuel. Below its ignition point it just
// sits there; raise a pool past ~493 K and it flashes to fire + smoke,
// and the flames spread across the whole pool. Classic liquid-fuel
// combustion — exactly why kerosene heaters and lamps work.
const KEROSENE_FIRE: Course = {
  id: "kerosene_fire",
  mode: "explorer",
  title: "Kerosene Catches Fire",
  summary: "Heat a pool of kerosene past its ignition point and watch it burn.",
  steps: [
    setup("kerosene_fire_setup", [
      // A shallow stone tray to hold the kerosene pool.
      { tick: 0, kind: "paint_rect", params: { material: "stone", x1: 80, y1: 196, x2: 176, y2: 202 } },
      { tick: 0, kind: "paint_rect", params: { material: "stone", x1: 80, y1: 184, x2: 84, y2: 202 } },
      { tick: 0, kind: "paint_rect", params: { material: "stone", x1: 172, y1: 184, x2: 176, y2: 202 } },
      // The kerosene pool.
      { tick: 0, kind: "paint_rect", params: { material: "kerosene", x1: 85, y1: 188, x2: 171, y2: 195 } },
      // Ground.
      { tick: 0, kind: "paint_rect", params: { material: "stone", x1: 0, y1: 250, x2: 255, y2: 255 } },
    ]),
    {
      kind: "narrate",
      says:
        "This is a tray of kerosene — a flammable liquid fuel, the stuff in lamps and jet engines. At room temperature it's perfectly calm; the molecules don't have enough energy to react with the oxygen around them. Let me change that with a spark of heat.",
      animation: "wave",
    },
    // A strong heat pulse pushes the pool well past kerosene's ~493 K
    // ignition point, so it flashes to fire.
    modify("kerosene_fire_ignite", {
      kind: "heat",
      params: { x: 128, y: 191, r: 14, kelvin: 1400 },
    }),
    { kind: "wait", ticks: 60 },
    {
      kind: "narrate",
      says:
        "Above its ignition temperature (~493 K / 220 °C) the kerosene vapour reacts with oxygen and flashes into flame: fuel + O₂ → CO₂ + water + a lot of heat and light. That heat lights the kerosene next to it, so the fire races across the whole pool.",
      animation: "happy",
    },
    { kind: "wait", ticks: 90 },
    {
      kind: "narrate",
      says:
        "See the dark plume above the flames? That's smoke — unburnt carbon and combustion gases rising on the hot updraft. Once the fuel is spent the fire dies. It's a self-sustaining chain: heat makes fire, fire makes heat — until the kerosene runs out.",
    },
  ],
};

// Float or Sink? — Archimedes' principle / solid buoyancy
// (FORMULAS.md B.2). Drops three solid blocks onto a pool: wax and ice
// (lighter than water) float at the surface, iron (much denser) sinks to
// the floor. Exercises the engine's new solid-on-liquid buoyancy: the
// rigid-body pass now lets a liquid hold up a body whose average density
// is below the fluid's, instead of every solid falling straight through.
const FLOAT_OR_SINK: Course = {
  id: "float_or_sink",
  mode: "inventor",
  title: "Float or Sink?",
  summary:
    "Drop wax, ice, and iron onto a pool — discover why some solids float and others sink.",
  steps: [
    setup("float_or_sink_setup", [
      // Ground.
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "stone", x1: 0, y1: 250, x2: 255, y2: 255 },
      },
      // Pool basin: floor + two side walls.
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "stone", x1: 40, y1: 210, x2: 216, y2: 214 },
      },
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "stone", x1: 36, y1: 120, x2: 40, y2: 214 },
      },
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "stone", x1: 216, y1: 120, x2: 220, y2: 214 },
      },
      // Fill it with water; surface sits at y=150.
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "water", x1: 41, y1: 150, x2: 215, y2: 209 },
      },
    ]),
    {
      kind: "narrate",
      says:
        "A pool of water — its density is about 1000 kg/m³. I've got three solid blocks to drop in. Archimedes' rule: a solid floats if it's lighter than the water it pushes aside, and sinks if it's heavier. Let's test it!",
      animation: "wave",
    },
    // Block 1 — wax (900 kg/m³): lighter than water, should float.
    modify("float_or_sink_wax", {
      kind: "paint_rect",
      params: { material: "wax", x1: 70, y1: 116, x2: 88, y2: 128 },
    }),
    { kind: "wait", ticks: 150 },
    {
      kind: "narrate",
      says:
        "The wax block fell to the surface and stopped — it's floating! Wax is about 900 kg/m³, less than water's 1000, so the water holds it up. The part below the waterline displaces just enough water to balance the block's weight.",
      animation: "happy",
    },
    // Block 2 — iron (7870 kg/m³): far denser, should sink.
    modify("float_or_sink_iron", {
      kind: "paint_rect",
      params: { material: "iron", x1: 168, y1: 116, x2: 186, y2: 128 },
    }),
    { kind: "wait", ticks: 150 },
    {
      kind: "narrate",
      says:
        "The iron block plunged straight to the bottom. At ~7870 kg/m³ it's nearly eight times denser than water — the water it pushes aside weighs far less than the iron, so there's nothing to hold it up. Lighter-than-water floats, heavier-than-water sinks.",
      animation: "happy",
    },
    // Block 3 — ice (917 kg/m³): the classic floater.
    modify("float_or_sink_ice", {
      kind: "paint_rect",
      params: { material: "ice", x1: 119, y1: 116, x2: 137, y2: 128 },
    }),
    { kind: "wait", ticks: 150 },
    {
      kind: "narrate",
      says:
        "Even solid ice floats — 917 kg/m³ against water's 1000. That tiny difference is why icebergs bob with most of their bulk hidden below the surface, and why a pond freezes from the top down instead of the bottom up. Same principle, every time: it's all about density.",
    },
  ],
};

// Radiant Cooling — Stefan-Boltzmann radiation (FORMULAS.md A.3).
// Heats a slab of fresh volcanic rock white-hot, then lets it shed that
// heat purely by radiation: it fades fast while glowing, then ever more
// slowly as it dims, because radiated power follows ε·σ·T⁴ — the fourth
// power of temperature. Exercises the new run_radiation_pass + hot_basalt's
// emissivity; the rock greys out to cold basalt below 700 K.
const RADIANT_COOLING: Course = {
  id: "radiant_cooling",
  mode: "genius",
  title: "Radiant Cooling",
  summary:
    "Heat volcanic rock white-hot and watch it radiate its heat away — fast at first, then slower (ε·σ·T⁴).",
  steps: [
    setup("radiant_cooling_setup", [
      // Ground.
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "stone", x1: 0, y1: 240, x2: 255, y2: 248 },
      },
      // A slab of (cool, dark) volcanic rock sitting on the ground.
      {
        tick: 0,
        kind: "paint_rect",
        params: { material: "hot_basalt", x1: 104, y1: 206, x2: 152, y2: 239 },
      },
    ]),
    {
      kind: "narrate",
      says:
        "This is a slab of fresh volcanic rock — basalt, just solidified from lava. Right now it's cool and dark. Watch what happens when I heat it white-hot...",
      animation: "wave",
    },
    modify("radiant_cooling_heat", {
      kind: "heat",
      params: { x: 128, y: 222, r: 32, kelvin: 1400 },
    }),
    {
      kind: "narrate",
      says:
        "Glowing! It's so hot it's giving off visible light. But it's also pouring out invisible infrared — losing heat by RADIATION. The power it sheds is j = ε·σ·T⁴: emissivity times the Stefan-Boltzmann constant times temperature to the fourth power.",
      animation: "happy",
    },
    { kind: "wait", ticks: 180 },
    {
      kind: "narrate",
      says:
        "See how quickly it dimmed? While it was white-hot it radiated furiously. But T⁴ cuts both ways — as it cools, the radiated power plummets. Drop the temperature by half and it radiates one-sixteenth as much. So the cooling starts fast and then drags.",
      animation: "happy",
    },
    { kind: "wait", ticks: 260 },
    {
      kind: "narrate",
      says:
        "Now it's dull grey, cold basalt again — no external wind or water needed, it simply glowed its heat away into the air. This is how a blacksmith's iron loses its colour, how a fresh lava flow crusts over, and how every star in the sky radiates: ε·σ·T⁴.",
    },
  ],
};

// Catalyst: Salt Speeds Rust — catalysis (FORMULAS.md C.3). Two sealed
// chambers, each with a thin iron column standing in an oxygen atmosphere so
// it rusts. The right column also has saltwater pressed against its other
// face — a catalyst that multiplies the rust rate ~100x without being
// consumed. The salted column reddens almost at once while the plain one
// barely starts. Exercises the new catalyst / catalyst_factor on iron_rusts.
const CATALYST_RUST: Course = {
  id: "catalyst_rust",
  mode: "genius",
  title: "Catalyst: Salt Speeds Rust",
  summary:
    "Two iron bars in oxygen — but salt touching one makes it rust ~100× faster without being used up.",
  steps: [
    setup("catalyst_rust_setup", [
      // --- Left chamber (control): iron in plain oxygen ---
      { tick: 0, kind: "paint_rect", params: { material: "stone", x1: 100, y1: 206, x2: 120, y2: 209 } },
      { tick: 0, kind: "paint_rect", params: { material: "stone", x1: 100, y1: 115, x2: 120, y2: 118 } },
      { tick: 0, kind: "paint_rect", params: { material: "stone", x1: 100, y1: 118, x2: 102, y2: 207 } },
      { tick: 0, kind: "paint_rect", params: { material: "stone", x1: 118, y1: 118, x2: 120, y2: 207 } },
      { tick: 0, kind: "paint_rect", params: { material: "oxygen", x1: 103, y1: 119, x2: 117, y2: 205 } },
      { tick: 0, kind: "paint_rect", params: { material: "iron", x1: 110, y1: 119, x2: 110, y2: 205 } },
      // --- Right chamber (catalysed): iron with oxygen on one face, saltwater on the other ---
      { tick: 0, kind: "paint_rect", params: { material: "stone", x1: 174, y1: 206, x2: 194, y2: 209 } },
      { tick: 0, kind: "paint_rect", params: { material: "stone", x1: 174, y1: 115, x2: 194, y2: 118 } },
      { tick: 0, kind: "paint_rect", params: { material: "stone", x1: 174, y1: 118, x2: 176, y2: 207 } },
      { tick: 0, kind: "paint_rect", params: { material: "stone", x1: 192, y1: 118, x2: 194, y2: 207 } },
      { tick: 0, kind: "paint_rect", params: { material: "oxygen", x1: 177, y1: 119, x2: 183, y2: 205 } },
      { tick: 0, kind: "paint_rect", params: { material: "saltwater", x1: 185, y1: 119, x2: 191, y2: 205 } },
      { tick: 0, kind: "paint_rect", params: { material: "iron", x1: 184, y1: 119, x2: 184, y2: 205 } },
    ]),
    {
      kind: "narrate",
      says:
        "Two sealed chambers, each with an iron bar standing in oxygen. Iron + oxygen → rust, but it's slow. The only difference: the bar on the RIGHT also has saltwater pressed against its other side. Watch which one rusts first...",
      animation: "wave",
    },
    { kind: "wait", ticks: 40 },
    {
      kind: "narrate",
      says:
        "The salty bar on the right is already red-brown with rust — while the plain bar on the left has barely changed! Saltwater is a catalyst: its dissolved ions ferry charge between the iron and the oxygen, slashing the reaction's activation energy.",
      animation: "happy",
    },
    { kind: "wait", ticks: 140 },
    {
      kind: "narrate",
      says:
        "Here's the key: count the saltwater — none of it was used up. A catalyst speeds a reaction without being consumed, so a tiny amount keeps working forever. It's why ships rust at sea, why your catalytic converter cleans exhaust, and why the enzymes in your body run chemistry that would otherwise take years.",
    },
  ],
};

// Lava Lamp: Convection — temperature-dependent density (FORMULAS.md B.1).
// Pink wax blobs sit at the bottom of a glass of water (they're denser when
// cool). Warm the base and the wax expands, drops below water's density, and
// floats up; at the cool top it shrinks, gets dense again, and sinks — the
// convection loop. Exercises the new thermal_expansion field + run_convection.
const LAVA_LAMP: Course = {
  id: "lava_lamp",
  mode: "inventor",
  title: "Lava Lamp: Convection",
  summary:
    "Warm the bottom of a glass and watch wax blobs rise and fall — the physics of convection.",
  steps: [
    setup("lava_lamp_setup", [
      // Tall glass container: floor + two side walls.
      { tick: 0, kind: "paint_rect", params: { material: "stone", x1: 96, y1: 208, x2: 160, y2: 214 } },
      { tick: 0, kind: "paint_rect", params: { material: "stone", x1: 92, y1: 70, x2: 96, y2: 214 } },
      { tick: 0, kind: "paint_rect", params: { material: "stone", x1: 160, y1: 70, x2: 164, y2: 214 } },
      // Water fills the glass.
      { tick: 0, kind: "paint_rect", params: { material: "water", x1: 97, y1: 78, x2: 159, y2: 207 } },
      // Two wax blobs resting on the bottom.
      { tick: 0, kind: "paint_rect", params: { material: "lava_wax", x1: 108, y1: 196, x2: 120, y2: 206 } },
      { tick: 0, kind: "paint_rect", params: { material: "lava_wax", x1: 136, y1: 196, x2: 148, y2: 206 } },
    ]),
    {
      kind: "narrate",
      says:
        "A tall glass of water with two blobs of pink wax sitting on the bottom. Cold, the wax is a touch denser than water, so it sinks and stays put. Now I'll gently warm the base...",
      animation: "wave",
    },
    modify("lava_lamp_heat1", {
      kind: "heat",
      params: { x: 128, y: 203, r: 38, kelvin: 360 },
    }),
    { kind: "wait", ticks: 90 },
    {
      kind: "narrate",
      says:
        "The warmed wax expanded — same mass, but spread into more space, so it's now LESS dense than the water around it. Less dense means it floats, and up the blobs go. Heat literally lifts them.",
      animation: "happy",
    },
    modify("lava_lamp_heat2", {
      kind: "heat",
      params: { x: 128, y: 203, r: 38, kelvin: 360 },
    }),
    { kind: "wait", ticks: 110 },
    {
      kind: "narrate",
      says:
        "Near the cool top the wax loses its heat, shrinks, turns dense again — and sinks back down toward the warmth. Up hot, down cold, round and round: that loop is convection.",
      animation: "happy",
    },
    modify("lava_lamp_heat3", {
      kind: "heat",
      params: { x: 128, y: 203, r: 38, kelvin: 360 },
    }),
    { kind: "wait", ticks: 110 },
    {
      kind: "narrate",
      says:
        "This same circulation — warm fluid rising, cool fluid sinking because density changes with temperature — drives ocean currents, the weather in the sky, the soup on your stove, and the churning interior of the Sun. One little glass, the whole planet's heat engine.",
    },
  ],
};

// Radiant Heat — the heating half of Stefan-Boltzmann radiation
// (FORMULAS.md A.3). A block of rock heated white-hot radiates heat across an
// air gap and sets a sheet of paper alight without ever touching it — and
// because radiated power scales as T⁴, the effect is ferocious. Exercises the
// new radiative-heating deposit in run_radiation_pass + hot_basalt emissivity.
const RADIANT_HEAT: Course = {
  id: "radiant_heat",
  mode: "genius",
  title: "Radiant Heat",
  summary:
    "Heat a rock white-hot and watch it set paper alight across a gap — by radiation alone (ε·σ·T⁴).",
  steps: [
    setup("radiant_heat_setup", [
      { tick: 0, kind: "paint_rect", params: { material: "stone", x1: 0, y1: 238, x2: 255, y2: 246 } },
      // White-hot rock furnace on the left.
      { tick: 0, kind: "paint_rect", params: { material: "hot_basalt", x1: 70, y1: 190, x2: 90, y2: 237 } },
      // A sheet of paper on the right, separated by a 1-cell air gap (x=91).
      { tick: 0, kind: "paint_rect", params: { material: "paper", x1: 92, y1: 190, x2: 104, y2: 237 } },
    ]),
    {
      kind: "narrate",
      says:
        "On the left: a block of rock. On the right, across an air gap: a sheet of paper. They're not touching, and there's no flame. Now I'll heat the rock white-hot and we'll just... watch the paper.",
      animation: "wave",
    },
    modify("radiant_heat_fire1", { kind: "heat", params: { x: 80, y: 213, r: 24, kelvin: 3000 } }),
    { kind: "wait", ticks: 40 },
    {
      kind: "narrate",
      says:
        "The glowing rock is pouring out invisible infrared — radiating heat as ε·σ·T⁴ — and it's streaming across the gap into the paper. Nothing is touching the paper, yet it's getting hotter and hotter...",
      animation: "happy",
    },
    modify("radiant_heat_fire2", { kind: "heat", params: { x: 80, y: 213, r: 24, kelvin: 3000 } }),
    { kind: "wait", ticks: 50 },
    modify("radiant_heat_fire3", { kind: "heat", params: { x: 80, y: 213, r: 24, kelvin: 3000 } }),
    { kind: "wait", ticks: 80 },
    {
      kind: "narrate",
      says:
        "Fire! The paper crossed its ignition point — about 506 K — purely from heat beamed across empty space. And that T⁴ is no small thing: double a source's temperature and it radiates SIXTEEN times the heat. It's how the Sun warms Earth across the void, why a bonfire scorches your face from yards away, and why a blast furnace glows so dangerously bright.",
    },
  ],
};

// Feather and the Hammer — free fall and terminal velocity (FORMULAS.md B.3).
// An iron hammer and a feather dropped together: the hammer plummets at the
// engine's full 1 cell/tick while the feather, with its low `fall_speed`,
// drifts down slowly. Air drag, not gravity, is what holds the feather back —
// the Apollo 15 / Galileo lesson. Exercises the new fall_speed terminal-
// velocity gate + the feather material.
const FEATHER_HAMMER: Course = {
  id: "feather_hammer",
  mode: "explorer",
  title: "The Feather & the Hammer",
  summary:
    "Drop a hammer and a feather together — and discover why one plummets while the other drifts.",
  steps: [
    setup("feather_hammer_setup", [
      { tick: 0, kind: "paint_rect", params: { material: "stone", x1: 0, y1: 240, x2: 255, y2: 248 } },
      // Iron hammer, high on the left.
      { tick: 0, kind: "paint_rect", params: { material: "iron", x1: 70, y1: 108, x2: 86, y2: 122 } },
      // A clump of feathers, the same height on the right.
      { tick: 0, kind: "paint_rect", params: { material: "feather", x1: 166, y1: 108, x2: 186, y2: 122 } },
    ]),
    {
      kind: "narrate",
      says:
        "A heavy iron hammer on the left, a clump of feathers on the right — both let go from the same height at the same instant. Gravity pulls on both. Which one reaches the ground first?",
      animation: "wave",
    },
    { kind: "wait", ticks: 140 },
    {
      kind: "narrate",
      says:
        "The hammer slammed down ages ago — but look, the feathers are still drifting lazily through the air! It's not that gravity is weaker on them. They're just so light and so spread-out that the air pushes back almost as hard as gravity pulls. They settle into a slow, steady 'terminal velocity'.",
      animation: "happy",
    },
    { kind: "wait", ticks: 240 },
    {
      kind: "narrate",
      says:
        "Here's the twist: take the air away and the difference vanishes. On the airless Moon, Apollo 15's commander really did drop a hammer and a feather together — and they hit the dust at the exact same moment. Galileo was right all along: without air, everything falls at the same rate. The feather's slowness was never about weight. It was about drag.",
    },
  ],
};

// Heat Capacity — metal vs water (FORMULAS.md A.1, Q = m·c·ΔT). Identical
// bonfires under an iron slab and a tank of water, a sheet of paper resting on
// each. Heat conducts up through both, but iron's low heat capacity lets its
// top race past paper's ignition point while the water — needing ~9× the
// energy per degree — barely warms, so its paper never lights. Exercises the
// new heat-capacity-weighted diffusion.
const HEAT_CAPACITY: Course = {
  id: "heat_capacity",
  mode: "explorer",
  title: "Metal vs Water: Heat Capacity",
  summary:
    "Same flame under iron and water — why does the metal scorch while the water shrugs it off?",
  steps: [
    setup("heat_capacity_setup", [
      { tick: 0, kind: "paint_rect", params: { material: "stone", x1: 0, y1: 246, x2: 255, y2: 252 } },
      // Left: an iron slab on a bonfire, paper on top.
      { tick: 0, kind: "paint_rect", params: { material: "bonfire", x1: 46, y1: 244, x2: 92, y2: 245 } },
      { tick: 0, kind: "paint_rect", params: { material: "iron", x1: 46, y1: 218, x2: 92, y2: 243 } },
      { tick: 0, kind: "paint_rect", params: { material: "paper", x1: 50, y1: 214, x2: 88, y2: 217 } },
      // Right: a tank of water on an identical bonfire, paper on top.
      { tick: 0, kind: "paint_rect", params: { material: "bonfire", x1: 160, y1: 244, x2: 212, y2: 245 } },
      { tick: 0, kind: "paint_rect", params: { material: "glass", x1: 156, y1: 200, x2: 160, y2: 243 } },
      { tick: 0, kind: "paint_rect", params: { material: "glass", x1: 212, y1: 200, x2: 216, y2: 243 } },
      { tick: 0, kind: "paint_rect", params: { material: "water", x1: 161, y1: 214, x2: 211, y2: 243 } },
      { tick: 0, kind: "paint_rect", params: { material: "paper", x1: 161, y1: 210, x2: 211, y2: 213 } },
    ]),
    {
      kind: "narrate",
      says:
        "Two identical bonfires. On the left, an iron slab; on the right, a tank of water — and a sheet of paper resting on each. Exactly the same heat goes into both. Will both papers catch fire?",
      animation: "wave",
    },
    { kind: "wait", ticks: 220 },
    {
      kind: "narrate",
      says:
        "The paper on the iron burst into flame — but the paper on the water is untouched! The heat raced up through the metal but the water just... swallowed it. Water has a huge heat capacity: it takes almost nine times as much energy to warm water by one degree as it does the same mass of iron.",
      animation: "happy",
    },
    { kind: "wait", ticks: 180 },
    {
      kind: "narrate",
      says:
        "That one property runs the world. It's why the ocean barely changes temperature between summer and winter and keeps coastal towns mild; why your body — mostly water — holds a steady 37 °C; and why engines and reactors are cooled with water. All that heat goes in, and water just shrugs.",
    },
  ],
};

// Frost & Dry Ice — deposition and sublimation (FORMULAS.md D.3). A sealed box
// of humid air (water vapour) and a metal chill-plate. Chill the plate below
// 0 °C and the vapour skips the liquid phase entirely, laying straight down as
// solid frost; warm it back up and the frost sublimes straight back to vapour,
// never passing through a puddle. Exercises the new `vapor_deposits_to_frost`
// and `frost_sublimes` reactions and the `frost` material.
const FROST_DEPOSITION: Course = {
  id: "frost_deposition",
  mode: "explorer",
  title: "Frost & Dry Ice: Skipping the Liquid",
  summary:
    "Make frost grow straight out of thin air on a cold plate — no liquid water in sight — then watch it vanish back into vapour.",
  steps: [
    setup("frost_deposition_setup", [
      // Ground.
      { tick: 0, kind: "paint_rect", params: { material: "stone", x1: 0, y1: 250, x2: 255, y2: 255 } },
      // Sealed box (walls + ceiling) so the water vapour can't escape.
      { tick: 0, kind: "paint_rect", params: { material: "stone", x1: 44, y1: 150, x2: 48, y2: 250 } },
      { tick: 0, kind: "paint_rect", params: { material: "stone", x1: 208, y1: 150, x2: 212, y2: 250 } },
      { tick: 0, kind: "paint_rect", params: { material: "stone", x1: 44, y1: 146, x2: 212, y2: 150 } },
      // A copper chill-plate sitting on the floor of the box.
      { tick: 0, kind: "paint_rect", params: { material: "copper", x1: 84, y1: 240, x2: 172, y2: 246 } },
      // Humid air: water vapour filling the box above the plate.
      { tick: 0, kind: "paint_rect", params: { material: "water_vapor", x1: 50, y1: 152, x2: 206, y2: 239 } },
    ]),
    {
      kind: "narrate",
      says:
        "This sealed box is full of water vapour — humidity, the invisible gas form of water. Down on the floor sits a copper plate. Normally to make ice you'd freeze liquid water… but there's no liquid here at all. Watch what happens when I chill the plate hard.",
      animation: "wave",
    },
    modify("frost_deposition_chill1", {
      kind: "set_temperature",
      params: { x: 128, y: 234, r: 52, kelvin: 220 },
    }),
    { kind: "wait", ticks: 90 },
    {
      kind: "narrate",
      says:
        "Frost! It's growing straight onto the cold plate out of the gas. The vapour got so cold it skipped being liquid entirely — gas straight to solid crystal. That's called deposition. It's exactly how frost feathers across a freezer wall or a winter windowpane, and how the very first ice crystals are born inside a snow cloud.",
      animation: "happy",
    },
    // Re-chill to keep the plate below freezing while more frost builds.
    modify("frost_deposition_chill2", {
      kind: "set_temperature",
      params: { x: 128, y: 234, r: 52, kelvin: 220 },
    }),
    { kind: "wait", ticks: 120 },
    {
      kind: "narrate",
      says:
        "The frost keeps thickening as long as the plate stays below 0 °C. Every vapour molecule that drifts close enough loses its energy to the cold metal and locks straight into the crystal. No melting, no dripping — the water never once becomes liquid.",
      animation: "happy",
    },
    // Now warm the plate well above freezing.
    modify("frost_deposition_warm", {
      kind: "set_temperature",
      params: { x: 128, y: 220, r: 70, kelvin: 320 },
    }),
    { kind: "wait", ticks: 160 },
    {
      kind: "narrate",
      says:
        "And now it runs in reverse: warm the frost and it doesn't melt into water — it vanishes straight back into vapour. That's sublimation, the same trick a block of dry ice plays as it smokes away to gas, or snow disappearing on a dry sunny day without leaving a puddle. Solid to gas, gas to solid — the whole cycle skips the liquid in the middle.",
    },
  ],
};

// Precipitation — silver chloride (FORMULAS.md C.12). Two clear solutions,
// saltwater and silver nitrate, meet and an insoluble white solid drops out:
// AgNO₃ + NaCl → AgCl↓ + NaNO₃. Exercises the new combine reaction and the
// silver_nitrate_solution / silver_chloride / sodium_nitrate materials.
const SILVER_PRECIPITATION: Course = {
  id: "silver_precipitation",
  mode: "genius",
  title: "Precipitation: Silver Chloride",
  summary:
    "Mix two clear liquids and watch a solid appear out of nowhere — the white curd of silver chloride.",
  steps: [
    setup("silver_precipitation_setup", [
      // Ground.
      { tick: 0, kind: "paint_rect", params: { material: "stone", x1: 0, y1: 250, x2: 255, y2: 255 } },
      // Glass beaker: floor + two walls (open top).
      { tick: 0, kind: "paint_rect", params: { material: "glass", x1: 92, y1: 204, x2: 164, y2: 208 } },
      { tick: 0, kind: "paint_rect", params: { material: "glass", x1: 92, y1: 150, x2: 96, y2: 204 } },
      { tick: 0, kind: "paint_rect", params: { material: "glass", x1: 160, y1: 150, x2: 164, y2: 204 } },
      // Clear saltwater filling the lower part of the beaker.
      { tick: 0, kind: "paint_rect", params: { material: "saltwater", x1: 97, y1: 178, x2: 159, y2: 203 } },
    ]),
    {
      kind: "narrate",
      says:
        "This beaker holds plain saltwater — sodium and chloride ions floating apart, completely clear and dissolved. I've got a second clear liquid up my sleeve: silver nitrate solution, just as see-through. Watch what happens the instant they meet.",
      animation: "wave",
    },
    // Pour a slug of silver nitrate solution in from above; it falls into the
    // beaker and mixes with the saltwater.
    modify("silver_precipitation_pour", {
      kind: "paint_rect",
      params: { material: "silver_nitrate_solution", x1: 116, y1: 120, x2: 140, y2: 146 },
    }),
    { kind: "wait", ticks: 70 },
    {
      kind: "narrate",
      says:
        "A white cloud is blooming wherever the two liquids touch — and it's solid! Two clear liquids just made something you can see, drifting down like snow. The silver ions grabbed the chloride ions and locked together into silver chloride, which simply won't dissolve — so it falls out of the water as a fine white curd.",
      animation: "happy",
    },
    { kind: "wait", ticks: 150 },
    {
      kind: "narrate",
      says:
        "This is precipitation — a 'double-displacement' reaction: AgNO₃ + NaCl → AgCl↓ + NaNO₃. The partners swap, and silver chloride is the one that can't stay dissolved, so it drops out while the sodium and nitrate ride along in the clear liquid. One more secret: silver chloride darkens in light. That single property made it the heart of black-and-white photographic film for over a century.",
    },
  ],
};

// Saponification — soap from fat + lye (FORMULAS.md C.21). A pot of animal fat
// with lye (sodium hydroxide) stirred through it in stripes. The lye attacks
// the fat and turns it to soap, releasing glycerin as a liquid byproduct.
// Exercises the new fat_saponifies combine reaction (and the recoloured soap).
const SOAP_MAKING: Course = {
  id: "soap_making",
  mode: "inventor",
  title: "Make Soap from Fat & Lye",
  summary:
    "Stir caustic lye into a pot of fat and watch it turn into soap — the oldest recipe in chemistry.",
  steps: [
    setup("soap_making_setup", [
      // Ground.
      { tick: 0, kind: "paint_rect", params: { material: "stone", x1: 0, y1: 250, x2: 255, y2: 255 } },
      // Stone pot: floor + two walls.
      { tick: 0, kind: "paint_rect", params: { material: "stone", x1: 70, y1: 200, x2: 186, y2: 206 } },
      { tick: 0, kind: "paint_rect", params: { material: "stone", x1: 70, y1: 168, x2: 74, y2: 200 } },
      { tick: 0, kind: "paint_rect", params: { material: "stone", x1: 182, y1: 168, x2: 186, y2: 200 } },
      // Fat filling the pot.
      { tick: 0, kind: "paint_rect", params: { material: "fat", x1: 75, y1: 174, x2: 181, y2: 199 } },
      // Lye (sodium hydroxide) stirred through it as stripes, so it contacts
      // fat above and below each band.
      { tick: 0, kind: "paint_rect", params: { material: "sodium_hydroxide", x1: 76, y1: 178, x2: 180, y2: 178 } },
      { tick: 0, kind: "paint_rect", params: { material: "sodium_hydroxide", x1: 76, y1: 183, x2: 180, y2: 183 } },
      { tick: 0, kind: "paint_rect", params: { material: "sodium_hydroxide", x1: 76, y1: 188, x2: 180, y2: 188 } },
      { tick: 0, kind: "paint_rect", params: { material: "sodium_hydroxide", x1: 76, y1: 193, x2: 180, y2: 193 } },
      { tick: 0, kind: "paint_rect", params: { material: "sodium_hydroxide", x1: 76, y1: 198, x2: 180, y2: 198 } },
    ]),
    {
      kind: "narrate",
      says:
        "A pot of plain animal fat, with streaks of lye — sodium hydroxide — stirred through it. Lye is nasty stuff: a strong base that burns skin. But mix it with fat and something useful happens. Let's give it a moment to react.",
      animation: "wave",
    },
    { kind: "wait", ticks: 120 },
    {
      kind: "narrate",
      says:
        "The fat is turning to soap! Wherever the lye touches it, the greasy fat is converting into a pale solid — and a slick liquid is seeping out alongside it. That liquid is glycerin, and the solid is soap. The lye has been used up doing it; that's why a finished bar of soap is gentle even though it was made with something caustic.",
      animation: "happy",
    },
    { kind: "wait", ticks: 120 },
    {
      kind: "narrate",
      says:
        "This is saponification: fat + lye → soap + glycerin. People have made soap this exact way for over four thousand years — boiling animal fat with ashes (a natural source of lye). The soap molecule has a greasy tail that grabs oils and a charged head that loves water, so it lifts grime off your hands and rinses it away. And the glycerin byproduct? It's saved and put into lotions and moisturisers.",
    },
  ],
};

// Mercury amalgamation of aluminium (FORMULAS.md C.5). A drop of mercury, and
// veins of it worked into an aluminium bar, dissolve the protective oxide skin
// so the bare metal grows white Al₂O₃ whiskers. Mercury is catalytic — it's
// never used up — so it keeps eating. Exercises the new
// mercury_amalgamates_aluminum reaction and the aluminum_oxide_whisker material.
const MERCURY_EATS_ALUMINUM: Course = {
  id: "mercury_eats_aluminum",
  mode: "genius",
  title: "Mercury Eats Aluminium",
  summary:
    "One drop of mercury makes a tough aluminium bar erupt into white fuzz — and it won't stop.",
  steps: [
    setup("mercury_eats_aluminum_setup", [
      // Ground.
      { tick: 0, kind: "paint_rect", params: { material: "stone", x1: 0, y1: 246, x2: 255, y2: 252 } },
      // A standing aluminium bar.
      { tick: 0, kind: "paint_rect", params: { material: "aluminum", x1: 112, y1: 96, x2: 144, y2: 245 } },
      // A drop of mercury on top, and two veins worked down into the metal.
      { tick: 0, kind: "paint_rect", params: { material: "mercury", x1: 118, y1: 88, x2: 138, y2: 96 } },
      { tick: 0, kind: "paint_rect", params: { material: "mercury", x1: 120, y1: 96, x2: 121, y2: 200 } },
      { tick: 0, kind: "paint_rect", params: { material: "mercury", x1: 135, y1: 96, x2: 136, y2: 200 } },
    ]),
    {
      kind: "narrate",
      says:
        "This is an aluminium bar — the same light, strong metal in drink cans and aircraft. It looks tough and shiny, but it has a hidden weakness. I've placed a drop of mercury on top and worked a little down into it. Watch closely.",
      animation: "wave",
    },
    { kind: "wait", ticks: 130 },
    {
      kind: "narrate",
      says:
        "White fuzz is erupting out of the metal! Aluminium is normally sealed by an invisible, ultra-thin skin of oxide that stops it corroding. But mercury soaks into the bare metal underneath and tears that skin open — and the freshly exposed aluminium reacts with the air, growing these feathery white whiskers of aluminium oxide. It gets genuinely hot while it does it, too.",
      animation: "happy",
    },
    { kind: "wait", ticks: 150 },
    {
      kind: "narrate",
      says:
        "And here's the wild part: the mercury is never used up. It keeps shuttling fresh metal to the surface, so one tiny drop can devour an entire aluminium beam given time. That's exactly why mercury is banned from passenger planes — a single broken thermometer could quietly eat through the aircraft's aluminium frame.",
    },
  ],
};

// Group-1 reactivity trend (FORMULAS.md C.13). Lithium (the gentle end) and
// rubidium (near the violent end) dropped into identical pools, side by side,
// so the periodic trend reads at a glance. Exercises the new rubidium reactions
// and the completed lithium pair.
const ALKALI_LADDER: Course = {
  id: "alkali_ladder",
  mode: "genius",
  title: "The Alkali Ladder: Lithium vs Rubidium",
  summary:
    "Drop two cousins from the same column of the periodic table into water — one fizzes, one detonates.",
  steps: [
    setup("alkali_ladder_setup", [
      // Ground.
      { tick: 0, kind: "paint_rect", params: { material: "stone", x1: 0, y1: 248, x2: 255, y2: 253 } },
      // Left pool.
      { tick: 0, kind: "paint_rect", params: { material: "stone", x1: 46, y1: 150, x2: 50, y2: 247 } },
      { tick: 0, kind: "paint_rect", params: { material: "stone", x1: 116, y1: 150, x2: 120, y2: 247 } },
      { tick: 0, kind: "paint_rect", params: { material: "stone", x1: 46, y1: 243, x2: 120, y2: 247 } },
      { tick: 0, kind: "paint_rect", params: { material: "water", x1: 51, y1: 160, x2: 115, y2: 242 } },
      // Right pool.
      { tick: 0, kind: "paint_rect", params: { material: "stone", x1: 136, y1: 150, x2: 140, y2: 247 } },
      { tick: 0, kind: "paint_rect", params: { material: "stone", x1: 206, y1: 150, x2: 210, y2: 247 } },
      { tick: 0, kind: "paint_rect", params: { material: "stone", x1: 136, y1: 243, x2: 210, y2: 247 } },
      { tick: 0, kind: "paint_rect", params: { material: "water", x1: 141, y1: 160, x2: 205, y2: 242 } },
      // Lithium into the left pool; rubidium into the right — same-sized chunks.
      { tick: 0, kind: "paint_rect", params: { material: "lithium", x1: 77, y1: 153, x2: 89, y2: 160 } },
      { tick: 0, kind: "paint_rect", params: { material: "rubidium", x1: 167, y1: 153, x2: 179, y2: 160 } },
    ]),
    {
      kind: "narrate",
      says:
        "Two identical pools of water. Into the left one I've dropped lithium; into the right, rubidium. They're cousins — both sit in Group 1 of the periodic table, the alkali metals, the same column. Same family, same chunk size. Watch how differently they greet the water.",
      animation: "wave",
    },
    { kind: "wait", ticks: 130 },
    {
      kind: "narrate",
      says:
        "What a difference! The lithium on the left is just fizzing quietly, bubbling off hydrogen gas. But the rubidium on the right detonated — a flash of fire and a billow of white smoke. It's the very same reaction with water (metal + water → hydrogen + heat), yet rubidium unleashes it violently enough to ignite the hydrogen on the spot.",
      animation: "happy",
    },
    { kind: "wait", ticks: 130 },
    {
      kind: "narrate",
      says:
        "This is one of the periodic table's most beautiful patterns. Going DOWN Group 1 — lithium, sodium, potassium, rubidium, caesium — the outer electron sits farther from the nucleus and gets easier to give away. So each metal reacts harder than the one above it: lithium fizzes, sodium zips and melts, potassium bursts into lilac flame, rubidium explodes, and caesium detonates. The table predicted that ladder before anyone had dropped them all in water.",
    },
  ],
};

// Liquid metals — gallium melting + mercury amalgamation (FORMULAS.md C.14). A
// solid gallium lump melts in "hand warmth", then a drop of mercury merges with
// it into a single silvery amalgam. Exercises the new gallium_melts /
// mercury_gallium_amalgamates reactions and the liquid_gallium / amalgam mats.
const LIQUID_METALS: Course = {
  id: "liquid_metals",
  mode: "inventor",
  title: "Liquid Metals: The Melting Spoon",
  summary:
    "Warm a chunk of metal until it melts like ice cream — then watch two liquid metals merge into one.",
  steps: [
    setup("liquid_metals_setup", [
      // Ground.
      { tick: 0, kind: "paint_rect", params: { material: "stone", x1: 0, y1: 246, x2: 255, y2: 252 } },
      // A little stone dish.
      { tick: 0, kind: "paint_rect", params: { material: "stone", x1: 100, y1: 206, x2: 156, y2: 212 } },
      { tick: 0, kind: "paint_rect", params: { material: "stone", x1: 100, y1: 184, x2: 104, y2: 206 } },
      { tick: 0, kind: "paint_rect", params: { material: "stone", x1: 152, y1: 184, x2: 156, y2: 206 } },
      // A lump of solid gallium in the dish.
      { tick: 0, kind: "paint_rect", params: { material: "gallium", x1: 106, y1: 190, x2: 150, y2: 205 } },
    ]),
    {
      kind: "narrate",
      says:
        "This shiny lump looks just like aluminium — but it's gallium, and it has a strange secret. Most metals need a furnace to melt. Gallium melts at just 29.8 °C, cooler than a hot day. So all it takes to turn it liquid is a bit of warmth, like the heat of your hand.",
      animation: "wave",
    },
    modify("liquid_metals_warm", {
      kind: "heat",
      params: { x: 128, y: 198, r: 30, kelvin: 360 },
    }),
    { kind: "wait", ticks: 80 },
    {
      kind: "narrate",
      says:
        "It's melting into a silvery puddle! A solid metal turning to liquid in your palm — that's the famous gallium 'disappearing spoon' prank: stir someone's tea with a gallium spoon and it quietly melts away. And unlike mercury, gallium isn't very toxic, so it's safe to hold.",
      animation: "happy",
    },
    modify("liquid_metals_add_mercury", {
      kind: "paint_rect",
      params: { material: "mercury", x1: 116, y1: 182, x2: 140, y2: 190 },
    }),
    { kind: "wait", ticks: 110 },
    {
      kind: "narrate",
      says:
        "Now I've added mercury — the only metal that's liquid at room temperature without any heating at all. Watch the two pools touch: they merge straight into each other, becoming a single silvery alloy called an amalgam. No flash, no oxide skin to fight through (the way aluminium has) — just two liquid metals dissolving into one. This is the gentle cousin of the violent mercury-and-aluminium reaction.",
    },
  ],
};

// "Hot ice" — crystallisation of a supersaturated solution (FORMULAS.md C.12).
// A clear, metastable sodium-acetate solution sits calm until a seed crystal
// touches it, then crystallises in a self-propagating exothermic wave (the
// reusable hand-warmer). Exercises the new sodium_acetate_crystallizes reaction
// and the sodium_acetate(_solution) materials.
const HOT_ICE: Course = {
  id: "hot_ice",
  mode: "explorer",
  title: "Hot Ice: Crystals from a Touch",
  summary:
    "Drop one tiny crystal into a clear liquid and watch a solid wave freeze the whole beaker — and get warm doing it.",
  steps: [
    setup("hot_ice_setup", [
      // Ground.
      { tick: 0, kind: "paint_rect", params: { material: "stone", x1: 0, y1: 250, x2: 255, y2: 255 } },
      // Glass beaker: floor + two walls.
      { tick: 0, kind: "paint_rect", params: { material: "glass", x1: 90, y1: 210, x2: 166, y2: 214 } },
      { tick: 0, kind: "paint_rect", params: { material: "glass", x1: 90, y1: 150, x2: 94, y2: 210 } },
      { tick: 0, kind: "paint_rect", params: { material: "glass", x1: 162, y1: 150, x2: 166, y2: 210 } },
      // Supersaturated solution filling the beaker.
      { tick: 0, kind: "paint_rect", params: { material: "sodium_acetate_solution", x1: 95, y1: 160, x2: 161, y2: 209 } },
    ]),
    {
      kind: "narrate",
      says:
        "This beaker is full of a special clear liquid — a supersaturated solution. It's holding far more dissolved solid than it normally could, like way too many people crammed into one room. It looks calm, but it's balanced on a knife-edge: all it needs is one tiny nudge to tip over.",
      animation: "wave",
    },
    // Drop a single seed crystal in from above.
    modify("hot_ice_seed", {
      kind: "paint_rect",
      params: { material: "sodium_acetate", x1: 126, y1: 150, x2: 130, y2: 154 },
    }),
    { kind: "wait", ticks: 130 },
    {
      kind: "narrate",
      says:
        "Watch it go! The instant that seed crystal touched the liquid, it triggered the cell beside it to crystallise too — and that one triggered its neighbours, and now a solid wave is tearing through the whole beaker. Liquid to solid in seconds, all from a single starting point.",
      animation: "happy",
    },
    { kind: "wait", ticks: 110 },
    {
      kind: "narrate",
      says:
        "And here's the twist — it's WARM. Locking molecules into a crystal releases stored energy as heat, so this 'ice' is actually hot, around 50 °C. That's exactly how a reusable hand-warmer works: click the little metal disc inside to seed it, and it sets solid and heats your pocket. Boil it back to a clear liquid and it's recharged, ready to do it all again.",
    },
  ],
};

// Curing / setting (FORMULAS.md Part H). Two moulds: wet cement (slow) and wet
// plaster (fast) poured side by side, flowing then hardening into concrete and
// gypsum. Exercises the new cement_cures / plaster_sets reactions and the
// cement_paste / plaster_paste materials.
const SETTING_CONCRETE: Course = {
  id: "setting_concrete",
  mode: "inventor",
  title: "Concrete & Plaster: From Slurry to Stone",
  summary:
    "Pour two runny slurries into moulds and watch them turn to solid stone — one in a flash, one slow and steady.",
  steps: [
    setup("setting_concrete_setup", [
      // Ground.
      { tick: 0, kind: "paint_rect", params: { material: "stone", x1: 0, y1: 250, x2: 255, y2: 255 } },
      // Left mould.
      { tick: 0, kind: "paint_rect", params: { material: "stone", x1: 46, y1: 222, x2: 114, y2: 228 } },
      { tick: 0, kind: "paint_rect", params: { material: "stone", x1: 46, y1: 192, x2: 50, y2: 222 } },
      { tick: 0, kind: "paint_rect", params: { material: "stone", x1: 110, y1: 192, x2: 114, y2: 222 } },
      // Right mould.
      { tick: 0, kind: "paint_rect", params: { material: "stone", x1: 142, y1: 222, x2: 210, y2: 228 } },
      { tick: 0, kind: "paint_rect", params: { material: "stone", x1: 142, y1: 192, x2: 146, y2: 222 } },
      { tick: 0, kind: "paint_rect", params: { material: "stone", x1: 206, y1: 192, x2: 210, y2: 222 } },
      // Wet cement poured above the left mould; wet plaster above the right.
      { tick: 0, kind: "paint_rect", params: { material: "cement_paste", x1: 58, y1: 162, x2: 102, y2: 188 } },
      { tick: 0, kind: "paint_rect", params: { material: "plaster_paste", x1: 154, y1: 162, x2: 198, y2: 188 } },
    ]),
    {
      kind: "narrate",
      says:
        "Two moulds. On the left I'm pouring wet cement — cement powder mixed with water; on the right, wet plaster. Right now both are runny slurries that flow and slosh like thick paint. Watch them fill the moulds… and then quietly turn to stone.",
      animation: "wave",
    },
    { kind: "wait", ticks: 150 },
    {
      kind: "narrate",
      says:
        "Look at the right mould — the plaster has already hardened into solid gypsum! That's why plaster of Paris is used for casts and quick repairs: it sets in minutes. The cement on the left is still a wet grey slurry, taking its time. Same idea, very different pace.",
      animation: "happy",
    },
    { kind: "wait", ticks: 500 },
    {
      kind: "narrate",
      says:
        "And now the cement has cured too, into hard grey concrete. Here's the surprise: neither of these 'dried' — they don't lose water, they lock it in. The powder reacts chemically with the water and grows interlocking crystals that bind everything into rock. That reaction, hydration, is why concrete can even set underwater. It cures slowly but ends up enormously strong — which is why concrete is, by weight, the most-used material humans make.",
    },
  ],
};

// Photosynthesis & respiration (FORMULAS.md C.16 / C.17) on the new engine
// light field. A leaf sealed in a glass jar of CO₂: in sunlight (through the
// glass) it photosynthesises CO₂ → O₂; shade it with an opaque lid and it
// reverses, respiring O₂ → CO₂. Exercises the new light_transmission material
// field and min_light / max_light reaction gates.
const PHOTOSYNTHESIS: Course = {
  id: "photosynthesis",
  mode: "genius",
  title: "Photosynthesis: A Plant Breathes Light",
  summary:
    "Seal a plant in a jar of carbon dioxide, give it sunlight, and watch it make oxygen — then take the light away.",
  steps: [
    setup("photosynthesis_setup", [
      // Ground.
      { tick: 0, kind: "paint_rect", params: { material: "stone", x1: 0, y1: 250, x2: 255, y2: 255 } },
      // Sealed glass jar (glass passes daylight through to the plant).
      { tick: 0, kind: "paint_rect", params: { material: "glass", x1: 90, y1: 206, x2: 166, y2: 210 } },
      { tick: 0, kind: "paint_rect", params: { material: "glass", x1: 90, y1: 150, x2: 94, y2: 206 } },
      { tick: 0, kind: "paint_rect", params: { material: "glass", x1: 162, y1: 150, x2: 166, y2: 206 } },
      { tick: 0, kind: "paint_rect", params: { material: "glass", x1: 90, y1: 150, x2: 166, y2: 154 } },
      // Carbon-dioxide air filling the jar.
      { tick: 0, kind: "paint_rect", params: { material: "carbon_dioxide", x1: 95, y1: 155, x2: 161, y2: 205 } },
      // A little leaf plant standing on the jar floor.
      { tick: 0, kind: "paint_rect", params: { material: "leaf", x1: 122, y1: 178, x2: 134, y2: 205 } },
    ]),
    {
      kind: "narrate",
      says:
        "A small plant sealed in a glass jar, and the jar is full of carbon dioxide — the grey gas we breathe out. Sunlight is streaming in through the glass. Keep your eye on the colour of the air inside.",
      animation: "wave",
    },
    { kind: "wait", ticks: 220 },
    {
      kind: "narrate",
      says:
        "The grey air is turning pale blue! The leaf is drinking in the carbon dioxide and giving off oxygen instead. That's photosynthesis: powered by sunlight, a plant takes CO₂ and water and builds its own food, releasing the oxygen as a leftover. Every breath of oxygen you take was made this way.",
      animation: "happy",
    },
    // Drop an opaque lid over the jar — total shade.
    modify("photosynthesis_shade", {
      kind: "paint_rect",
      params: { material: "stone", x1: 84, y1: 142, x2: 172, y2: 150 },
    }),
    { kind: "wait", ticks: 220 },
    {
      kind: "narrate",
      says:
        "Now I've capped the jar in shade, and watch — it runs backwards! In the dark the plant can't photosynthesise, so it just breathes like we do: taking in oxygen and giving off CO₂, and the air greys over again. Plants actually do both all the time; it's only because sunlight makes photosynthesis win so overwhelmingly that forests and ocean plankton end up being the planet's lungs.",
    },
  ],
};

// Water photolysis (FORMULAS.md C.22) under an artificial UV lamp — exercising
// both the light field's point-light sources and a photocatalyst. A titania bed
// under water, lit by a uv_lamp, splits the water into hydrogen + oxygen.
const SOLAR_HYDROGEN: Course = {
  id: "solar_hydrogen",
  mode: "genius",
  title: "Splitting Water with Light",
  summary:
    "Shine a UV lamp on the right white powder and watch it tear water apart into hydrogen and oxygen.",
  steps: [
    setup("solar_hydrogen_setup", [
      // A sealed underground reactor (stone roof blocks the sky entirely).
      { tick: 0, kind: "paint_rect", params: { material: "stone", x1: 0, y1: 250, x2: 255, y2: 255 } },
      { tick: 0, kind: "paint_rect", params: { material: "stone", x1: 72, y1: 154, x2: 184, y2: 160 } },
      { tick: 0, kind: "paint_rect", params: { material: "stone", x1: 72, y1: 160, x2: 78, y2: 212 } },
      { tick: 0, kind: "paint_rect", params: { material: "stone", x1: 178, y1: 160, x2: 184, y2: 212 } },
      { tick: 0, kind: "paint_rect", params: { material: "stone", x1: 72, y1: 206, x2: 184, y2: 212 } },
      // A bed of titanium dioxide (the photocatalyst) on the floor, water above.
      { tick: 0, kind: "paint_rect", params: { material: "titania", x1: 80, y1: 200, x2: 176, y2: 205 } },
      { tick: 0, kind: "paint_rect", params: { material: "water", x1: 80, y1: 164, x2: 176, y2: 199 } },
      // A submerged UV lamp bar just above the catalyst bed, so its light
      // actually reaches the water touching the powder.
      { tick: 0, kind: "paint_rect", params: { material: "uv_lamp", x1: 100, y1: 192, x2: 156, y2: 198 } },
    ]),
    {
      kind: "narrate",
      says:
        "A sealed underground tank of water, sitting on a bed of white powder — titanium dioxide, the same harmless stuff in sunscreen and white paint. No sunlight reaches down here. But that violet bar glowing in the tank isn't an ordinary lamp: it's pouring out ultraviolet light right onto the powder.",
      animation: "wave",
    },
    { kind: "wait", ticks: 200 },
    {
      kind: "narrate",
      says:
        "Bubbles! The water touching the lit powder is being torn apart into two gases. The titanium dioxide is a 'photocatalyst' — by itself it does nothing, and water in the dark does nothing, but powder + light together rips water molecules (H₂O) into hydrogen and oxygen. Notice there's about twice as much hydrogen rising as oxygen — because every water molecule carries two H's for each O.",
      animation: "happy",
    },
    { kind: "wait", ticks: 160 },
    {
      kind: "narrate",
      says:
        "You just watched one of the great hopes for clean energy. If the lamp were the Sun, this would be 'solar water splitting' — using light to bottle hydrogen, a fuel that burns to nothing but water again. No smoke, no carbon. Honda's labs and others have chased exactly this reaction on titanium dioxide for decades.",
    },
  ],
};

// Phosphorescence — glow-in-the-dark (FORMULAS.md C.37). A bed of glow pigment
// charges under a UV lamp, then keeps glowing (lighting its own surroundings)
// once the lamp is off, slowly fading. Exercises both halves of the light field
// — the pigment reads light to charge, and the charged form emits it.
const GLOW_IN_THE_DARK: Course = {
  id: "glow_in_the_dark",
  mode: "explorer",
  title: "Glow in the Dark",
  summary:
    "Charge a bed of glow powder under a lamp, switch the lamp off, and watch the dark room keep glowing on its own.",
  steps: [
    setup("glow_in_the_dark_setup", [
      // A sealed dark room.
      { tick: 0, kind: "paint_rect", params: { material: "stone", x1: 0, y1: 250, x2: 255, y2: 255 } },
      { tick: 0, kind: "paint_rect", params: { material: "stone", x1: 60, y1: 118, x2: 196, y2: 124 } },
      { tick: 0, kind: "paint_rect", params: { material: "stone", x1: 60, y1: 124, x2: 66, y2: 212 } },
      { tick: 0, kind: "paint_rect", params: { material: "stone", x1: 190, y1: 124, x2: 196, y2: 212 } },
      { tick: 0, kind: "paint_rect", params: { material: "stone", x1: 60, y1: 206, x2: 196, y2: 212 } },
      // A bed of (spent, pale) glow pigment on the floor.
      { tick: 0, kind: "paint_rect", params: { material: "phosphor", x1: 70, y1: 199, x2: 186, y2: 205 } },
      // A UV lamp just above it.
      { tick: 0, kind: "paint_rect", params: { material: "uv_lamp", x1: 84, y1: 191, x2: 172, y2: 197 } },
    ]),
    {
      kind: "narrate",
      says:
        "A bed of pale glow powder in a sealed, dark room — and a UV lamp switched on right above it. The powder looks dull grey-green for now. But watch it as it sits under that ultraviolet light…",
      animation: "wave",
    },
    { kind: "wait", ticks: 120 },
    {
      kind: "narrate",
      says:
        "It's lit up bright green! The pigment is soaking up the lamp's light and storing the energy inside its crystals. Now for the magic — I'll switch the lamp off and plunge the room into darkness.",
      animation: "happy",
    },
    // Switch the lamp off (paint over it).
    modify("glow_in_the_dark_lamp_off", {
      kind: "paint_rect",
      params: { material: "stone", x1: 84, y1: 191, x2: 172, y2: 197 },
    }),
    { kind: "wait", ticks: 320 },
    {
      kind: "narrate",
      says:
        "The lamp is off, the room is dark — and the powder is still glowing, lighting up the gloom all by itself! That stored energy is leaking back out as light. This is phosphorescence: the secret of glow-in-the-dark stars, watch hands and exit signs. It traps light while it's bright and pays it back slowly in the dark — see how it's already dimming as the stored charge runs down.",
    },
  ],
};

// ---------------------------------------------------------------

export const COURSES: Course[] = [
  // Starter
  BOIL_WATER,
  FREEZE_WATER,
  FALLING_SAND,
  LIGHT_CANDLE,
  // Explorer
  BURN_WOOD,
  FEATHER_HAMMER,
  ICE_OVER_LAVA,
  SALT_DISSOLVES,
  IRON_RUSTS,
  HEAT_THE_SNOW,
  KEROSENE_FIRE,
  HEAT_CAPACITY,
  FROST_DEPOSITION,
  HOT_ICE,
  GLOW_IN_THE_DARK,
  // Inventor
  VOLCANO,
  TORCH_LINE,
  MOUNTAIN_WINTER,
  POOL_BY_BONFIRE,
  OIL_FLOATS,
  FLOAT_OR_SINK,
  LAVA_LAMP,
  IRON_CRUSHES_WOOD,
  BOTTLE_VOLCANO,
  SOAP_MAKING,
  LIQUID_METALS,
  SETTING_CONCRETE,
  // Genius
  HYDROGEN_OXYGEN,
  ACID_BASE,
  METHANE_COMBUSTION,
  SODIUM_IN_WATER,
  MERCURY_SINKS,
  CAESIUM_EXPLODES,
  RADIANT_COOLING,
  CATALYST_RUST,
  RADIANT_HEAT,
  SILVER_PRECIPITATION,
  MERCURY_EATS_ALUMINUM,
  ALKALI_LADDER,
  PHOTOSYNTHESIS,
  SOLAR_HYDROGEN,
];

export function coursesForMode(mode: Course["mode"]): Course[] {
  return COURSES.filter((c) => c.mode === mode);
}
